import { Button, Grid, Paper, Typography } from '@mui/material';
import { SiTrackPage } from '../../components/si-track-page';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { workOderReportColumns } from './work-order-report-columns';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { WorkOrderFilters } from './work-order.filters';
import { UserPermissionName } from '../../models/user-permission-name';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { WorkOrderReport } from '../../models/work-order-report';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  workorder: any;
}

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};
export const WorkOrderReportPage = () => {
  const isMultiSelectReportEnabled = process.env.REACT_APP_MULTI_SELECT_REPORT_FLAG === 'true';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      user: { userID: '', fullName: '' },
      service: { serviceID: '', serviceName: '' },
      dateCriteria: isMultiSelectReportEnabled ? [{ dateCriteriaValue: '', dateCriteria: '' }] : { dateCriteriaValue: '', dateCriteria: '' },
      workOrderStatus: isMultiSelectReportEnabled ? [{ workOrderStatusID: '', workOrderStatus: '' }] : { workOrderStatusID: '', workOrderStatus: '' },
      projectStatus: { statusValue: 'active', statusLabel: 'Active' },
      workOrderActiveStatus: { statusValue: 'active', statusLabel: 'Active' },
      action: '',
      // scheduledDateFrom: '',
      // scheduledDateTo: '',
    },
  });

  const { isLoading, data, mutateAsync: mutateWorkOrderAsync } = useSaveData<any>(workorderReportApiKeyStore.getWorkOrderReports());

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      customerID: filtersData?.customer?.customerID ? filtersData?.customer?.customerID : 0,
      officeID: filtersData?.office?.officeID ? filtersData?.office?.officeID : 0,
      projectID: filtersData?.project?.projectID ? filtersData?.project?.projectID : 0,
      unitID: filtersData?.unit?.unitID ? filtersData?.unit?.unitID : 0,
      serviceID: filtersData?.service?.serviceID ? filtersData?.service?.serviceID : 0,
      inspectorID: filtersData?.user?.userID ? filtersData?.user?.userID : 0,
      woStatusIDs: isMultiSelectReportEnabled ? filtersData?.workOrderStatusID?.map((s: any) => s?.workOrderStatusID) ?? [] :
        [filtersData?.workOrderStatus?.workOrderStatusID ? filtersData?.workOrderStatus?.workOrderStatusID : 0],
      dateCriterias: isMultiSelectReportEnabled ? filtersData?.dateCriteria?.map((s: any) => s?.dateCriteriaValue) ?? [] :
        [filtersData?.dateCriteria?.dateCriteriaValue ?? 0],
      dateFrom: filtersData?.fromDate ?? '',
      dateTo: filtersData?.toDate ?? '',
      projectStatus: filtersData?.projectStatus?.statusValue ?? '',
      workOrderActiveStatus: filtersData?.workOrderActiveStatus?.statusValue ?? '',
      // scheduledDateFrom: filtersData?.scheduledDateFrom ?? '',
      // scheduledDateTo: filtersData?.scheduledDateTo ?? '',
    };
    mutateWorkOrderAsync({ data: filteringData });
    setSelectedFilters(filteringData);
  };

  const handleShowAll = () => {
    form.handleSubmit(handleSubmit)();
  };

  const WorkOrderCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickWorkorderNumber = () => {
      navigate(`/workorders?searchType=workorders&searchText=${workorder?.workOrderNumber}`);
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.workOrderNumber}
        </Typography>
      </td>
    );
  };

  const InvoiceAmountCell = ({ workorder }: CellProps) => {
    const invoiceAmount = workorder?.invoiceAmountNumeric;

    const formattedAmount =
      invoiceAmount !== undefined && invoiceAmount !== null
        ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const PriceAmountCell = ({ workorder }: CellProps) => {
    const priceAmount = workorder?.priceNumeric;

    const formattedAmount =
      priceAmount !== undefined && priceAmount !== null
        ? `$${parseFloat(priceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const AccuralAmountCell = ({ workorder }: CellProps) => {
    const accrualAmount = workorder?.accrualAmountNumeric;

    const formattedAmount =
      accrualAmount !== undefined && accrualAmount !== null
        ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      window.open(`/units/profile/${workorder?.unitID}`, "_blank");
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  // const WorkHoursCell = ({ workorder }: CellProps) => {
  //   const accrualAmount = workorder?.workedHoursNumeric;

  //   const formattedAmount =
  //     accrualAmount !== undefined && accrualAmount !== null
  //       ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
  //       : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
  //   return (
  //     <td>
  //       <Typography component="span">{formattedAmount}</Typography>
  //     </td>
  //   );
  // };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'workOrderNumber') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <WorkOrderCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmountNumeric') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <InvoiceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'accrualAmountNumeric') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <AccuralAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'priceNumeric') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <PriceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'jobsite') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }

    // if (field === 'workedHoursNumeric') {
    //   // Return a function that takes a data item and returns a JSX element
    //   return (dataItem: any) => {
    //     return <WorkHoursCell workorder={dataItem.dataItem} />;
    //   };
    // }

    return undefined;
  };


  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('WorkOrderReport', 'WorkOrderReport')
  );

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredWorkOrderReports, setFilteredWorkOrderReports] = useState<WorkOrderReport[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredWorkOrderReportList = data?.data?.filter((WorkOrderReport: any) =>
      Object.values(WorkOrderReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredWorkOrderReports(filteredWorkOrderReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'WorkOrderReport',
      pageName: 'WorkOrderReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'WorkOrderReport',
      pageName: 'WorkOrderReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage title="Work Order Reports">
      <ReactHookForm formName="WORK_ORDER_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <WorkOrderFilters onShowAllWorkOrder={handleShowAll} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='work_order_report'
                columns={workOderReportColumns}
                dataItemKey="workOrderID"
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                isBusy={isLoading || isLoadingGridLayout}

                // -----------------Grid Layout-------
                dataItems={filteredWorkOrderReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};
